// src/features/fx-info/api.ts
//
// Data fetcher for the single-asset FX Info (Tear Sheet) page. Talks to the
// FastAPI fx-info router via the shared `apiClient` axios instance, which:
//   - attaches the WordPress-issued JWT (Authorization: Bearer <token>) so the
//     auth-gated /api/v1/fx-info router is reached as an authorized user, and
//   - already bakes the `/api/v1` version prefix into its baseURL.
// We therefore request the bare `/fx-info/...` path here — adding `/api/v1`
// would double the prefix. See api/client.ts (REQUIRED_API_PREFIX).
//
// These interfaces mirror the backend Pydantic response models in
// app/domain/fx_info/schemas.py 1:1 — keep them in lock-step so TypeScript
// gives perfect autocomplete and the frontend does zero heavy math.

import { apiClient } from '../../api/client';

// -------------------------------------------------------------------------
// Pillar 1: Institutional Volatility Cone
// -------------------------------------------------------------------------
export interface VolatilityConePoint {
    window_days: number;
    window_label: string;
    current_vol: number;
    min_vol: number;
    p25_vol: number;
    median_vol: number;
    p75_vol: number;
    max_vol: number;
}

// -------------------------------------------------------------------------
// Pillar 2: Seasonality & Return Statistics
// -------------------------------------------------------------------------
export interface FxSeasonalityMonth {
    month: number;
    month_label: string;
    average_return: number;
    median_return: number;
    win_rate: number;
    observation_count: number;
}

// -------------------------------------------------------------------------
// Pillar 3 & 4: Core Time-Series Array (Chart Payloads)
// -------------------------------------------------------------------------
export interface FxTimeSeriesPoint {
    date: string;
    close: number;
    frac_diff: number | null;
    sma_50: number | null;
    sma_200: number | null;
    drawdown: number;
    net_commercial: number | null;
    net_noncomm: number | null;
    positioning_z_score_3yr: number | null;
}

// -------------------------------------------------------------------------
// Master Tear Sheet Response
// -------------------------------------------------------------------------
export interface FxInfoResponse {
    symbol: string;
    fmp_ticker: string;
    name: string;
    tier: string;
    factor_exposures: string[];
    invert_price: boolean;
    as_of_date: string;
    current_price: number;
    ytd_return: number;
    volatility_cone: VolatilityConePoint[];
    seasonality: FxSeasonalityMonth[];
    time_series: FxTimeSeriesPoint[];
}

// Bare path — the shared apiClient baseURL already includes `/api/v1`.
const BASE_URL = '/fx-info';

// Fetcher: the full analytical tear sheet for a single currency (e.g. "JPY").
// Case-insensitive on the backend; returns 404 for symbols outside the
// institutional 30-currency universe or without ingested pricing.
export const fetchFxInfoBySymbol = async (
    symbol: string,
): Promise<FxInfoResponse> => {
    const response = await apiClient.get<FxInfoResponse>(
        `${BASE_URL}/${encodeURIComponent(symbol.toUpperCase())}`,
    );
    return response.data;
};
